import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiUsers, FiPlusCircle, FiTrash2, FiLock, FiPhone, FiMail, FiX, FiEdit2, FiMapPin, FiDollarSign } from 'react-icons/fi';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';

function SettingsAccounts() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [shopSettings, setShopSettings] = useState(null);

  // Admin form state
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [adminForm, setAdminForm] = useState({ 
    username: '', password: '', role: 'admin',
    fullName: '', phone: '', email: '', address: ''
  });
  
  // Password modal state (for admin to set new password directly)
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ adminId: '', adminUsername: '', newPassword: '' });

  // Salary history modal state
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [salaryData, setSalaryData] = useState({ admin: null, expenses: [], total: 0, loading: false });

  useEffect(() => {
    loadShopSettings();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadShopSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/shop-settings`);
      if (response.data.success) {
        setShopSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error loading shop settings:', error);
    }
  };

  const openAdminModal = (admin = null) => {
    if (admin) {
      // Edit mode
      setEditingAdmin(admin);
      setAdminForm({
        username: admin.username,
        password: '',
        role: admin.role || 'admin',
        fullName: admin.fullName || '',
        phone: admin.phone || '',
        email: admin.email || '',
        address: admin.address || ''
      });
    } else {
      // Add mode
      setEditingAdmin(null);
      setAdminForm({ 
        username: '', password: '', role: 'admin',
        fullName: '', phone: '', email: '', address: ''
      });
    }
    setShowAdminModal(true);
  };

  const handleSaveAdmin = async (e) => {
    e.preventDefault();
    
    if (editingAdmin) {
      // Update existing admin - only update profile info
      try {
        await axios.put(`${API_URL}/shop-settings/admins/${editingAdmin._id}/profile`, {
          role: adminForm.role,
          fullName: adminForm.fullName,
          phone: adminForm.phone,
          email: adminForm.email,
          address: adminForm.address
        });
        toast.success('Đã cập nhật thông tin tài khoản');
        setShowAdminModal(false);
        setEditingAdmin(null);
        loadShopSettings();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Không thể cập nhật tài khoản');
      }
    } else {
      // Create new admin
      if (!adminForm.password) {
        toast.error('Vui lòng nhập mật khẩu');
        return;
      }
      try {
        await axios.post(`${API_URL}/shop-settings/admins`, {
          username: adminForm.username,
          password: adminForm.password,
          role: adminForm.role,
          fullName: adminForm.fullName,
          phone: adminForm.phone,
          email: adminForm.email,
          address: adminForm.address
        });
        toast.success('Đã thêm tài khoản');
        setShowAdminModal(false);
        setAdminForm({ 
          username: '', password: '', role: 'admin',
          fullName: '', phone: '', email: '', address: ''
        });
        loadShopSettings();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Không thể thêm tài khoản');
      }
    }
  };

  const handleDeleteAdmin = async (id) => {
    if (!window.confirm('Xóa tài khoản này?')) return;
    try {
      await axios.delete(`${API_URL}/shop-settings/admins/${id}`);
      toast.success('Đã xóa tài khoản');
      loadShopSettings();
    } catch (error) {
      toast.error('Không thể xóa');
    }
  };

  const openPasswordModal = (admin) => {
    setPasswordForm({ adminId: admin._id, adminUsername: admin.username, newPassword: '' });
    setShowPasswordModal(true);
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (!passwordForm.newPassword) {
      toast.error('Vui lòng nhập mật khẩu mới');
      return;
    }
    try {
      await axios.put(`${API_URL}/shop-settings/admins/${passwordForm.adminId}/set-password`, {
        newPassword: passwordForm.newPassword
      });
      toast.success('Đã đặt mật khẩu mới');
      setShowPasswordModal(false);
      setPasswordForm({ adminId: '', adminUsername: '', newPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể đặt mật khẩu');
    }
  };

  const openSalaryModal = async (admin) => {
    setSalaryData({ admin, expenses: [], total: 0, loading: true });
    setShowSalaryModal(true);
    
    try {
      // Build recipient name to search
      const recipientName = admin.fullName 
        ? `${admin.fullName} - ${admin.username}` 
        : admin.username;
      
      const response = await axios.get(`${API_URL}/expenses/by-recipient/${encodeURIComponent(recipientName)}`);
      
      if (response.data.success) {
        setSalaryData({
          admin,
          expenses: response.data.data || [],
          total: response.data.total || 0,
          loading: false
        });
      } else {
        setSalaryData(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error loading salary history:', error);
      setSalaryData(prev => ({ ...prev, loading: false }));
      toast.error('Không thể tải lịch sử chi lương');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  return (
    <div className={`space-y-6 ${isMobile ? 'pb-20' : ''}`}>
      <h1 className="text-2xl font-bold text-gray-800">Quản lý tài khoản</h1>

      <div className="bg-white rounded-lg border p-4 md:p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-lg flex items-center">
            <FiUsers className="mr-2 text-purple-600" />
            Tài khoản đăng nhập
          </h3>
          <button onClick={() => openAdminModal()} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center">
            <FiPlusCircle className="mr-2" /> Thêm tài khoản
          </button>
        </div>

        <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
          <p><strong>Admin:</strong> Toàn quyền truy cập hệ thống</p>
          <p><strong>Bán hàng:</strong> Không xem được báo cáo, giá vốn, lãi lỗ, cài đặt</p>
        </div>

        {shopSettings?.additionalAdmins?.length === 0 ? (
          <p className="text-gray-500 text-center py-8">Chưa có tài khoản bổ sung nào</p>
        ) : (
          <div className="space-y-3">
            {shopSettings?.additionalAdmins?.map(admin => (
              <div key={admin._id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-bold text-lg">{admin.username}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${admin.role === 'sales' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                        {admin.role === 'sales' ? 'Bán hàng' : 'Admin'}
                      </span>
                    </div>
                    {admin.fullName && <p className="text-gray-600 mt-1">{admin.fullName}</p>}
                    <div className="text-sm text-gray-500 mt-1 space-y-0.5">
                      {admin.phone && (
                        <div><FiPhone className="inline mr-1" size={12} />{admin.phone}</div>
                      )}
                      {admin.email && (
                        <div><FiMail className="inline mr-1" size={12} />{admin.email}</div>
                      )}
                      {admin.address && (
                        <div><FiMapPin className="inline mr-1" size={12} />{admin.address}</div>
                      )}
                    </div>
                    <p className="text-gray-400 text-xs mt-2">Tạo lúc: {new Date(admin.createdAt).toLocaleDateString('vi-VN')}</p>
                  </div>
                  <div className="flex flex-col md:flex-row gap-1">
                    {admin.role === 'sales' && (
                      <button 
                        onClick={() => openSalaryModal(admin)} 
                        className="text-orange-600 hover:bg-orange-50 px-3 py-1 rounded flex items-center text-sm"
                        title="Xem lịch sử chi lương"
                      >
                        <FiDollarSign className="mr-1" /> Lương
                      </button>
                    )}
                    <button 
                      onClick={() => openAdminModal(admin)} 
                      className="text-green-600 hover:bg-green-50 px-3 py-1 rounded flex items-center text-sm"
                    >
                      <FiEdit2 className="mr-1" /> Sửa
                    </button>
                    <button 
                      onClick={() => openPasswordModal(admin)} 
                      className="text-blue-600 hover:bg-blue-50 px-3 py-1 rounded flex items-center text-sm"
                    >
                      <FiLock className="mr-1" /> Đặt MK
                    </button>
                    <button 
                      onClick={() => handleDeleteAdmin(admin._id)} 
                      className="text-red-600 hover:bg-red-50 px-3 py-1 rounded flex items-center text-sm"
                    >
                      <FiTrash2 className="mr-1" /> Xóa
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Admin Modal - Add/Edit */}
      {showAdminModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{editingAdmin ? 'Sửa tài khoản' : 'Thêm tài khoản'}</h2>
              <button onClick={() => { setShowAdminModal(false); setEditingAdmin(null); }} className="text-gray-400 hover:text-gray-600">
                <FiX size={24} />
              </button>
            </div>
            <form onSubmit={handleSaveAdmin} className="space-y-4">
              <div>
                <label className="block font-semibold mb-2">Tên đăng nhập *</label>
                <input
                  type="text"
                  value={adminForm.username}
                  onChange={(e) => setAdminForm({ ...adminForm, username: e.target.value })}
                  className={`w-full border rounded-lg px-4 py-2 ${editingAdmin ? 'bg-gray-100' : ''}`}
                  required
                  disabled={!!editingAdmin}
                />
                {editingAdmin && (
                  <p className="text-xs text-gray-500 mt-1">Không thể thay đổi tên đăng nhập</p>
                )}
              </div>

              <div>
                <label className="block font-semibold mb-2">Loại tài khoản *</label>
                <select
                  value={adminForm.role}
                  onChange={(e) => setAdminForm({ ...adminForm, role: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2"
                  required
                >
                  <option value="admin">Admin - Toàn quyền</option>
                  <option value="sales">Bán hàng - Giới hạn quyền</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {adminForm.role === 'sales' 
                    ? 'Không xem được: báo cáo, giá vốn, lãi lỗ, cài đặt' 
                    : 'Có toàn quyền truy cập hệ thống'}
                </p>
              </div>

              {/* Password field - only show when adding new account */}
              {!editingAdmin && (
                <div>
                  <label className="block font-semibold mb-2">Mật khẩu *</label>
                  <input
                    type="text"
                    value={adminForm.password}
                    onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                    className="w-full border rounded-lg px-4 py-2"
                    placeholder="Nhập mật khẩu"
                    required
                  />
                </div>
              )}

              <hr className="my-4" />
              <p className="text-sm text-gray-500 mb-2">Thông tin cá nhân (tùy chọn)</p>

              <div>
                <label className="block text-sm font-medium mb-1">Họ và tên</label>
                <input
                  type="text"
                  value={adminForm.fullName}
                  onChange={(e) => setAdminForm({ ...adminForm, fullName: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2"
                  placeholder="Nhập họ và tên"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Số điện thoại</label>
                <input
                  type="tel"
                  value={adminForm.phone}
                  onChange={(e) => setAdminForm({ ...adminForm, phone: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2"
                  placeholder="Nhập số điện thoại"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  value={adminForm.email}
                  onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2"
                  placeholder="Nhập email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Địa chỉ</label>
                <input
                  type="text"
                  value={adminForm.address}
                  onChange={(e) => setAdminForm({ ...adminForm, address: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2"
                  placeholder="Nhập địa chỉ"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => { setShowAdminModal(false); setEditingAdmin(null); }} className="px-4 py-2 border rounded-lg">
                  Hủy
                </button>
                <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">
                  {editingAdmin ? 'Cập nhật' : 'Thêm'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Set Password Modal - Admin sets password directly without knowing old password */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Đặt mật khẩu mới</h2>
              <button onClick={() => setShowPasswordModal(false)} className="text-gray-400 hover:text-gray-600">
                <FiX size={24} />
              </button>
            </div>
            
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-blue-800 text-sm">
                Đặt mật khẩu mới cho tài khoản: <strong>{passwordForm.adminUsername}</strong>
              </p>
            </div>

            <form onSubmit={handleSetPassword} className="space-y-4">
              <div>
                <label className="block font-semibold mb-2">Mật khẩu mới *</label>
                <input
                  type="text"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2"
                  placeholder="Nhập mật khẩu mới"
                  required
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">Mật khẩu hiển thị dạng text để dễ nhập</p>
              </div>

              <div className="flex justify-end space-x-3">
                <button type="button" onClick={() => setShowPasswordModal(false)} className="px-4 py-2 border rounded-lg">
                  Hủy
                </button>
                <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">
                  Đặt mật khẩu
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Salary History Modal */}
      {showSalaryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold flex items-center">
                <FiDollarSign className="mr-2 text-orange-600" />
                Lịch sử chi lương
              </h2>
              <button onClick={() => setShowSalaryModal(false)} className="text-gray-400 hover:text-gray-600">
                <FiX size={24} />
              </button>
            </div>
            
            <div className="mb-4 p-3 bg-orange-50 rounded-lg">
              <p className="text-orange-800">
                Nhân viên: <strong>{salaryData.admin?.fullName || salaryData.admin?.username}</strong>
                {salaryData.admin?.fullName && <span className="text-gray-500 ml-2">({salaryData.admin?.username})</span>}
              </p>
            </div>

            {salaryData.loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">Đang tải...</p>
              </div>
            ) : salaryData.expenses.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FiDollarSign size={48} className="mx-auto mb-2 opacity-30" />
                <p>Chưa có khoản chi lương nào</p>
              </div>
            ) : (
              <>
                {/* Summary */}
                <div className="mb-4 p-4 bg-green-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-green-800 font-medium">Tổng đã chi:</span>
                    <span className="text-green-800 font-bold text-xl">{formatCurrency(salaryData.total)}</span>
                  </div>
                  <p className="text-green-600 text-sm mt-1">{salaryData.expenses.length} lần chi</p>
                </div>

                {/* Expenses List */}
                <div className="space-y-2">
                  {salaryData.expenses.map((expense, index) => (
                    <div key={expense._id || index} className="border rounded-lg p-3 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-medium">{expense.name}</p>
                          <p className="text-sm text-gray-500">
                            {formatDate(expense.expenseDate)}
                            {expense.expenseGroup?.name && (
                              <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded text-xs">
                                {expense.expenseGroup.name}
                              </span>
                            )}
                          </p>
                          {expense.notes && (
                            <p className="text-sm text-gray-400 mt-1">{expense.notes}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-orange-600">{formatCurrency(expense.amount)}</p>
                          <p className="text-xs text-gray-400">
                            {expense.paymentMethod === 'cash' ? 'Tiền mặt' : 'Chuyển khoản'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="flex justify-end mt-4 pt-4 border-t">
              <button 
                onClick={() => setShowSalaryModal(false)} 
                className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SettingsAccounts;
